import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { dateSchema, idSchema, objectIdSchema } from '../../validation';

/**
 * Lifecycle of one LLM turn.
 *
 * - `running`         — graph is executing; `Conversation.lockedBy.runId`
 *                       points here.
 * - `completed`       — final assistant message committed.
 * - `failed`          — an error escaped to the SSE handler.
 * - `cancelled`       — interrupted (e.g. voice channel `interrupt`
 *                       policy or user clicked Stop).
 * - `awaiting_human`  — `escalate_to_human` was called; not a failure
 *                       state but the AI is no longer driving.
 */
export const agentRunStatusSchema = z.enum([
  'running',
  'completed',
  'failed',
  'cancelled',
  'awaiting_human',
]);
export type AgentRunStatus = z.infer<typeof agentRunStatusSchema>;

/**
 * `AgentRun` — one row per LLM turn.
 *
 * The conversation lock points here (`Conversation.lockedBy.runId`).
 * Multiple runs can attach to one `Conversation` over its lifetime;
 * each run gets its own LangGraph checkpoint thread
 * (`thread_id = conversationId:runId`).
 *
 * See `docs/ai/concurrency.md` and `docs/ai/observability.md`.
 */
export const AgentRunSchema = createModel({
  conversationId: objectIdSchema,
  /**
   * Snapshot of `Conversation.org` at run time — real Org only,
   * absent for guest / org-less threads (same contract as
   * `ConversationSchema.org`).
   */
  org: partySchema.optional(),
  userId: objectIdSchema,

  status: agentRunStatusSchema.default('running'),

  startedAt: dateSchema,
  completedAt: dateSchema.optional(),

  /** Model id — e.g. `claude-sonnet-5`. */
  model: idSchema,

  errorMessage: z.string().optional(),

  /**
   * Opaque token used by the SSE handler to re-attach a dropped
   * stream to the same in-flight run. Indexed unique-sparse so a
   * lookup is constant-time.
   */
  resumeToken: idSchema.optional(),
});

export type AgentRun = z.infer<typeof AgentRunSchema.model>;
export type AgentRunData = z.infer<typeof AgentRunSchema.data>;
export type AgentRunPartial = z.infer<typeof AgentRunSchema.partial>;
export type AgentRunQueryOne = QueryOne<AgentRun>;
export type AgentRunQueryMany = QueryMany<AgentRun>;
